<script>
import useSharedData from "@/use/SharedData.js";
import useTools from "@/use/Tools.js";
import {useSecurityStore} from "@/stores/Security.js"
import {ref, watch} from "vue";
import useSharedMethod from "@/use/SharedMethod.js";
import {useMedicalCompanionStore} from "@/stores/Medical/MedicalCompanion.js";
import MedicalCompanionFilter from "@/views/Medical/Companion/Components/MedicalCompanionFilter.vue";
import axios from "axios";

export default {
    components: {MedicalCompanionFilter},
    setup() {
        const {
            loading,
            total,
            filter,
            itemsPerPage
        } = useSharedData();

        const {transformFilterValues, formatedDate} = useSharedMethod();

        const {$router, $i18n} = useTools();

        const medicalCompanionStore = useMedicalCompanionStore();
        const securityStore = useSecurityStore();

        const headers = [
            {
                text: "...",
                sortable: false,
                align: "left",
                value: "action",
                width: "2%"
            },
            {
                text: $i18n.t("common.code"),
                align: "left",
                value: "code",
                sortable: true,
                width: "0%"
            },
            {
                text: $i18n.t("common.label"),
                align: "left",
                value: "name",
                sortable: true,
                width: "0%"
            },
            {
                text: $i18n.t("module.medical_reason"),
                align: "left",
                value: "reasonName",
                sortable: true,
                width: "0%"
            },
            {
                text: $i18n.t("module.medical_center"),
                align: "left",
                value: "centerName",
                sortable: true,
                width: "0%"
            },
            {
                text: $i18n.t("common.start"),
                align: "left",
                value: "start",
                sortable: true,
                width: "0%"
            },
            {
                text: $i18n.t("common.end"),
                align: "left",
                value: "end",
                sortable: true,
                width: "0%"
            },
            {
                text: $i18n.t("common.comment"),
                align: "left",
                value: "observation",
                sortable: true,
            },
        ];

        const actions = [
            {
                title: $i18n.t("common.edit"),
                icon: 'mdi-pencil-box-multiple',
                color: 'orange',
                access: () => true,
                disabled: () => false,
                func: (item) => {
                    $router.push({name: 'medical_companions.edit', params: {id: item.id}})
                },
            },
            {
                title: $i18n.t("common.delete"),
                icon: 'mdi-trash-can',
                color: 'red',
                access: () => true,
                disabled: () => false,
                func: (item) => {
                    $router.push({name: 'medical_companions.delete', params: {id: item.id}})
                },
            },
            {
                title: $i18n.t("module.edms"),
                icon: 'mdi-folder',
                color: 'amber',
                access: () => true,
                disabled: () => false,
                func: (item) => {
                    $router.push({name: 'medical_companions.documents', params: {id: item.id}})
                },
            },
            {
                title: $i18n.t("module.template_application"),
                icon: 'mdi-text-box-multiple',
                color: 'teal',
                access: () => true,
                disabled: () => false,
                func: (item) => {
                    $router.push({name: 'medical_companions.template_applications', params: {id: item.id}})
                },
            },
        ];


        const searchParams = ref({
            sortBy: "start",
            descending: true,
            page: 1,
            itemsPerPage: itemsPerPage
        });

        function handleSearch(params) {
            loading.value = true;
            medicalCompanionStore.setMedicalCompanions([]);
            total.value = 0;


            filter.value.page = params.page - 1;
            filter.value.rowsPerPage = params.itemsPerPage;
            filter.value.sortBy = params.sortBy;
            filter.value.descending = params.descending;

            axios.get('/medicalCompanions', {params: transformFilterValues(filter.value)}).then((response) => {
                medicalCompanionStore.setMedicalCompanions(response.data.elements);
                total.value = response.data.total;
            }).finally(() => {
                loading.value = false;
            });
        }

        function findAll() {
            handleSearch(searchParams.value);
        }

        watch(() => [filter.value], () => {
            searchParams.page = 1;
            findAll();
        }, {deep: true, immediate: true});

        watch(searchParams, (newParams) => {
            handleSearch(newParams);
        }, {deep: true});



        function clearFilter() {
            Object.keys(filter.value).forEach(key => {
                filter.value[key] = null;
            });
        }

        function view(item) {
            $router.push({name: 'medical_companions.view', params: {id: item.id}});
        }



        return {
            medicalCompanionStore,
            securityStore,
            headers,
            loading,
            total,
            filter,
            searchParams,
            actions,
            findAll,
            clearFilter,
            view,
            formatedDate,
        };
    },
};
</script>

<template>
    <div>
        <w-app
            :title="$t('module.medical_companion')"
            show-total
            :total="total"
            show-filter
            :filter="filter"
            @clearFilter="clearFilter"
        >
            <template v-slot:filter>
                <MedicalCompanionFilter v-model="filter"/>
            </template>

            <template v-slot:rightToolbar>
                <w-btn-reload
                    :loading="loading"
                    @press="findAll"
                />
                <w-btn-add
                    :show="true"
                    @press="$router.push({ name: 'medical_companions.add' })"
                />
            </template>

            <template v-slot:content>
                <w-server-data-table
                    v-model="searchParams"
                    :actions="actions"
                    :headers="headers"
                    :items="medicalCompanionStore.medicalCompanions"
                    :loading="loading"
                    :total="total"
                    root-path="medical_companions"
                    module="MEDICAL_COMPANION"
                    @rowDbClick="view"
                >
                    <template v-slot:[`item.code`]="{ item }">
                        <w-chip :text="item.code"/>
                    </template>
                    <template v-slot:[`item.name`]="{ item }">
                        <w-inline-text bold :text="item.name"/>
                    </template>
                    <template v-slot:[`item.start`]="{ item }">
                        <w-inline-text :text="formatedDate(item.start)"/>
                    </template>
                    <template v-slot:[`item.end`]="{ item }">
                        <w-inline-text :text="formatedDate(item.end)"/>
                    </template>
                    <template v-slot:[`item.centerName`]="{ item }">
                        <w-inline-text :text="item.centerName"/>
                    </template>
                    <template v-slot:[`item.reasonName`]="{ item }">
                        <w-inline-text :text="item.reasonName"/>
                    </template>
                </w-server-data-table>
            </template>
        </w-app>

        <router-view/>
    </div>
</template>
